import { createCn } from "cn/config";

/**
 * The design system's typography scale (`../styles.css`).
 *
 * The merge engine has to be told about these by name. Its default rules decide
 * whether `text-foo` is a SIZE or a COLOUR by pattern — a t-shirt size like
 * `text-sm` is a size, and anything it doesn't recognise falls through to
 * colour. Every name here (`text-body-sm`, `text-label-sm`, …) falls through.
 *
 * The consequence was silent and everywhere: in any `cn()` that combined one of
 * these with a semantic colour — `cn("text-label-sm", done && "text-success-text")`
 * — merge saw two colours, kept the last, and DELETED the size. The element
 * then rendered at the inherited size. On the To Do rail that showed up as
 * labels jumping from 11px to 14px, but nothing about it is specific to that
 * feature: it applies to every call site that styles size and colour together.
 *
 * Keep in step with the `--text-*` tokens in the stylesheet. A name missing
 * here doesn't error — it just quietly goes back to being treated as a colour.
 */
const sfcFontSizes = [
  "heading-xl",
  "heading-lg",
  "heading-md",
  "heading-sm",
  "body-lg",
  "body-md",
  "body-sm",
  "label-lg",
  "label-md",
  "label-sm",
  "mono-md",
  "mono-sm",
] as const;

export const cn = createCn({
  extend: {
    classGroups: {
      "font-size": [{ text: [...sfcFontSizes] }],
    },
  },
});

export function getInitials(name?: string | null) {
  if (!name) {
    return "";
  }

  return name
    .split(" ")
    .map((word) => word[0])
    .concat(name[1])
    .join("")
    .substring(0, 2)
    .toUpperCase();
}

/** Deterministic avatar-ish color (FNV-1a hash → hue) for a string key. */
export function generateColorFromString(value: string): string {
  let hash = 0x811c9dc5;

  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = (hash * 0x01000193) | 0;
  }

  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 65%, 45%)`;
}

export function pad(num: number, padding: string = "00"): string {
  if (num < 0) {
    return "-" + pad(-num, padding);
  }

  const str = num.toString();

  if (str.length >= padding.length) {
    return str;
  }

  return (padding + str).slice(-padding.length);
}
